/**
 * Brand-identity atoms for the ClawInstall property.
 *
 * The requesting host's {@link BrandIdentity} is resolved server-side (root
 * `beforeLoad`) and seeded into {@link brandIdentityAtom} per request by
 * `RootDocument` via `useAtomInitialValues` — the same SSR-seeded-atom pattern
 * as `cookieConsentAtom`, so no `useState`/`useEffect` and no hydration
 * mismatch. Components read the identity reactively via `useAtomValue`.
 *
 * @since 1.0.0
 */
import { Atom } from "@effect-atom/atom-react";
import { BRAND_IDENTITIES, type BrandIdentity } from "~/config/domains.ts";

/**
 * Identity of the requesting host; `null` outside the ClawInstall property.
 * Seedable base atom — every dependent seeds THIS atom (per request).
 *
 * `keepAlive` is load-bearing: `RootDocument` seeds it with `useAtomInitialValues`
 * (a one-shot `setValue`, no subscriber) and pages read it during the hydration
 * render, but `useSyncExternalStore` only subscribes in a passive effect. In
 * between, the registry's node-removal microtask drops every unsubscribed
 * non-`keepAlive` node, so the subscription re-created this atom at its `null`
 * default and consinna.ai hydrated as ClawInstall (2026-09-09). The registry is
 * per request on the server (`RootComponent` mounts `RegistryProvider`), so a
 * kept-alive node never leaks an identity across requests.
 *
 * @since 1.0.0
 */
export const brandIdentityAtom = Atom.make<BrandIdentity | null>(null).pipe(Atom.keepAlive);

/**
 * Identity for components that only render inside the ClawInstall property —
 * defaults to the ClawInstall identity (also the value unit tests see when
 * nothing is seeded). Derived, so it cannot be seeded directly.
 *
 * @since 1.0.0
 */
export const clawInstallIdentityAtom = Atom.map(
  brandIdentityAtom,
  (identity) => identity ?? BRAND_IDENTITIES.clawinstall,
);
